import * as React from 'react';

import {
  enumPurchaseLineItemType,
  enumUnit,
} from '@rematter/frontend-lib-api-client';
import { cn } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $deleteEntry,
  $itemsInterfaceDisabled,
  $itemsInterfaceInventoryLocked,
  $itemsInterfacePricingLocked,
  $itemsInterfaceShowPricing,
  $updateFreightCharge,
} from '../_state';
import {
  enumLineItemKind,
  NormalizedFreightCharge,
  NormalizedLineItem,
} from '../_types';
import {
  ChargebackField,
  DeleteButtonField,
  FreightEditButtonField,
  InventoryItemField,
} from '../fields';
import {
  getItemRowGridCols,
  ITEM_ROW_ERROR_BORDER,
  ITEM_ROW_FOCUS_WITHIN,
} from '../layout';
import { ItemRow } from '../layout/ItemRow';

import { $openFreightDetails } from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type ItemRowFreightProps = {
  freightCharge: NormalizedFreightCharge;
  autoFocus?: boolean;
  onFocusRow?: (id: string) => void;
  hasError?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ ITEM ROW FREIGHT ⚛️ ⚛️ ⚛️
//
export const ItemRowFreight: React.FC<ItemRowFreightProps> = ({
  freightCharge,
  autoFocus = false,
  onFocusRow,
  hasError,
}) => {
  const t = useTranslations();

  // Refs
  const chargebackRef = React.useRef<HTMLDivElement>(null);

  // Atoms
  const disabled = useAtomValue($itemsInterfaceDisabled);
  const inventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const pricingLocked = useAtomValue($itemsInterfacePricingLocked);
  const showPricing = useAtomValue($itemsInterfaceShowPricing);
  const onUpdateFreightCharge = useSetAtom($updateFreightCharge);
  const onDeleteEntry = useSetAtom($deleteEntry);
  const openFreightDetails = useSetAtom($openFreightDetails);

  // Memoized values
  const stubLineItem: NormalizedLineItem = React.useMemo(
    () => ({
      kind: enumLineItemKind.LineItem,
      id: freightCharge.id,
      type: enumPurchaseLineItemType.Freight,
      itemId: null,
      tagId: null,
      weights: null,
      unitPrice: null,
      pricingUnit: enumUnit.Pounds,
    }),
    [freightCharge.id],
  );

  const freightFieldProps = {
    freightCharge,
    onUpdateFreightCharge,
    onDeleteFreightCharge: onDeleteEntry,
  };

  const gridClassName = getItemRowGridCols(showPricing);

  // Callbacks
  const noopUpdate = React.useCallback(() => null, []);

  const handleOpenDialog = React.useCallback(() => {
    openFreightDetails({
      lineItemId: freightCharge.id,
      notes: freightCharge.notes,
    });
  }, [freightCharge.id, freightCharge.notes, openFreightDetails]);

  // Effects
  /** Auto-focus the chargeback input when this row is newly added */
  React.useEffect(() => {
    if (!autoFocus) return;

    const inputElement = chargebackRef?.current?.querySelector('input');
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 100);
    }
  }, [autoFocus]);

  return (
    <ItemRow
      gridTemplate={gridClassName}
      className={cn(
        '@ItemRowFreight relative',
        ITEM_ROW_FOCUS_WITHIN,
        hasError && ITEM_ROW_ERROR_BORDER,
      )}
      onFocus={() => onFocusRow?.(freightCharge.id)}
    >
      <InventoryItemField
        lineItem={stubLineItem}
        onUpdateItem={noopUpdate}
        onDeleteItem={onDeleteEntry}
        disabled={disabled}
        symbolDescription={
          t.scaleTicketPro.inventoryItemField.fixedFreightCharge
        }
      />

      <div
        onClick={handleOpenDialog}
        className={cn(
          'flex h-full items-center rounded-md bg-neutral/5 p-3 text-left text-sm text-neutral/60',
          'hover:bg-neutral/5',
          showPricing && 'col-span-4',
          !showPricing && 'col-span-3',
        )}
      >
        <span className="line-clamp-2">
          {freightCharge.notes || t.scaleTicketPro.freightDetails.fields.notes}
        </span>
      </div>

      {showPricing && (
        <>
          <div ref={chargebackRef}>
            <ChargebackField
              {...freightFieldProps}
              disabled={disabled || pricingLocked}
            />
          </div>
          <div />
        </>
      )}

      <div />
      <FreightEditButtonField
        freightCharge={freightCharge}
        disabled={disabled}
      />
      <div />
      <DeleteButtonField
        lineItem={stubLineItem}
        onUpdateItem={noopUpdate}
        onDeleteItem={onDeleteEntry}
        disabled={disabled || inventoryLocked}
      />
    </ItemRow>
  );
};
